
import Vue from "vue";
import { mapGetters, mapActions } from "vuex";
import { Plan } from "@/lib/models/user";
import { NolaAnalytics, NolaItemId } from "@/lib/utils/analytics";
import { AssistAnalytics } from "@/lib/utils/analytics/assist";
import CatIcon from "@/components/atoms/CatIcon.vue";
import { useTabPurchaseSync } from "@/composables/useTabPurchaseSync";

export default Vue.extend({
  name: "FishCounter",
  components: {
    CatIcon,
  },
  data() {
    return {
      nolaAnalytics: new NolaAnalytics(this.$gtm, this.$store),
      assistAnalytics: null as AssistAnalytics | null,
      catIconSize: "small",
      // Composable関数の参照を保持
      tabPurchaseSyncFunctions: null as any,
    };
  },
  computed: {
    ...mapGetters("fishModule", ["currentFish", "isLoading", "error"]),
    ...mapGetters("billingModule", ["plan"]),
    ...mapGetters("fishModule", ["isShowFishPurchaseCoachMark"]),
    ...mapGetters("writerTypeResultModule", {
      writerType: "writerType",
    }),
    /**
     * フォーマット済みのcurrentFish
     */
    formattedCurrentFish(): string {
      return (this.currentFish as number).toLocaleString("ja-JP");
    },
    /**
     * 無料プランかどうかを判定
     */
    isFree() {
      const currentPlan = this.plan as Plan;
      return currentPlan === Plan.free;
    },
    isShowCoachMark() {
      return this.isShowFishPurchaseCoachMark;
    },
  },
  watch: {
    currentFish: {
      handler(newValue: number) {
        // currentFish が 5,000 未満になった時にコーチマークを表示
        if (newValue < 5000) {
          this.$store.dispatch("fishModule/showFishPurchaseCoachMark");
        }
      },
    },
  },
  async mounted() {
    // アナリティクス初期化
    this.assistAnalytics = new AssistAnalytics(this.$gtm);

    // Composableを初期化
    this.initializeTabPurchaseSync();

    // コンポーネントがマウントされた時にFishデータを取得
    try {
      await this.fetchFishData();
    } catch (error) {
      console.error("Fish データの初期取得に失敗:", error);
    }
  },
  beforeDestroy() {
    // TabPurchaseSyncのクリーンアップ
    if (this.tabPurchaseSyncFunctions?.cleanup) {
      this.tabPurchaseSyncFunctions.cleanup();
    }
  },
  methods: {
    ...mapActions("fishModule", ["fetchFishData"]),
    async handleAddClick(): Promise<void> {
      try {
        // アナリティクス: プレミアムボタンクリックイベントの発火
        if (this.assistAnalytics) {
          this.assistAnalytics.trackPremiumButtonClick();
        }

        // プレミアムボタンクリック時にSubscriptionAnnounceページを別タブで開く
        // 執筆中の作業を中断させないよう配慮
        const subscriptionUrl = this.$router.resolve({ name: "subscriptionAnnounce" }).href;
        window.open(subscriptionUrl, "_blank");
      } catch (error) {
        console.error("プレミアムボタンクリック処理に失敗:", error);
      }
    },
    async handlePurchaseFish(): Promise<void> {
      // アナリティクス: Fish 購入ボタンクリックイベントの発火
      this.nolaAnalytics.logButtonEvent(NolaItemId.AssistFooterPurchaseFish);

      // Fish 購入ページを別タブで開く
      const subscriptionUrl = this.$router.resolve({ name: "purchaseFish" }).href;
      window.open(subscriptionUrl, "_blank");
    },
    handleCloseCoachMark(): void {
      this.$store.dispatch("fishModule/closeFishPurchaseCoachMark");
    },
    // タブ復帰同期の初期化（mounted後に実行）
    initializeTabPurchaseSync(): void {
      // Composableを初期化してthisが利用可能な同期コールバックを設定
      this.tabPurchaseSyncFunctions = useTabPurchaseSync(
        async () => {
          // thisが利用可能な状態でFishデータを取得
          try {
            await this.fetchFishData();
          } catch (error) {
            console.error("[FishCounter] Failed to fetch Fish data:", error);
          }
        },
        { enableDebugLog: false }
      );
    },
  },
});
